import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { ComboboxFilter } from '@/components/shared/ComboboxFilter';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuthStore } from '@/stores/auth';
import { useDashboardRouteSearch } from '../hooks/useDashboardRouteSearch';
import { DASHBOARD_PERIOD_LABELS, DASHBOARD_PERIOD_OPTIONS, type DashboardPeriod } from '../service/types';
import { DashboardDisplayStatusCard } from './DashboardDisplayStatusCard';
import {
  formatCustomDateRangeButton,
  getCustomDateRangeFromIso,
  toManilaDayBoundaryIso,
} from '../utils/date-range';

const LazyCreateDealDialog = lazy(() =>
  import('@/features/deals').then((module) => ({
    default: module.CreateDealDialog,
  })),
);

function QuickDealAction() {
  const { t } = useTranslation();
  const [shouldLoadDialog, setShouldLoadDialog] = useState(false);
  const [openAfterLoad, setOpenAfterLoad] = useState(false);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!shouldLoadDialog || !openAfterLoad || !triggerButtonRef.current) {
      return;
    }

    triggerButtonRef.current.click();
    setOpenAfterLoad(false);
  }, [shouldLoadDialog, openAfterLoad]);

  if (!shouldLoadDialog) {
    return (
      <Button
        size="sm"
        onClick={() => {
          setShouldLoadDialog(true);
          setOpenAfterLoad(true);
        }}
      >
        {t('dashboard.actions.quick_deal')}
      </Button>
    );
  }

  return (
    <Suspense fallback={<Button size="sm" disabled>{t('dashboard.actions.loading')}</Button>}>
      <LazyCreateDealDialog trigger={<Button ref={triggerButtonRef} size="sm">{t('dashboard.actions.quick_deal')}</Button>} />
    </Suspense>
  );
}

export function DashboardPeriodActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { period, from, to } = useDashboardRouteSearch();
  const activeBranchId = useAuthStore((state) => state.activeBranchId);
  const effectiveBranchId =
    activeBranchId && activeBranchId !== 'all' ? activeBranchId : undefined;
  const [customRange, setCustomRange] = useState<DateRange>();
  const [isCustomRangeOpen, setCustomRangeOpen] = useState(false);

  const periodOptions = DASHBOARD_PERIOD_OPTIONS.map((option) => ({
    value: option,
    label: DASHBOARD_PERIOD_LABELS[option],
  }));

  const selectedCustomRange = customRange ?? getCustomDateRangeFromIso(from, to);
  const customRangeFrom = selectedCustomRange?.from;
  const customRangeTo = selectedCustomRange?.to;
  const isCustomRangeValid =
    customRangeFrom instanceof Date &&
    customRangeTo instanceof Date &&
    customRangeFrom.getTime() <= customRangeTo.getTime();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <QuickDealAction />

      <DashboardDisplayStatusCard branchId={effectiveBranchId} variant="compact" />

      <ComboboxFilter
        title={t('dashboard.actions.period')}
        value={period}
        options={periodOptions}
        clearOnReselect={false}
        onValueChange={(nextValue) => {
          if (!DASHBOARD_PERIOD_OPTIONS.includes(nextValue as DashboardPeriod)) return;

          const nextPeriod = nextValue as DashboardPeriod;
          if (nextPeriod === 'custom') {
            setCustomRange(getCustomDateRangeFromIso(from, to));
            setCustomRangeOpen(true);
            return;
          }

          setCustomRangeOpen(false);
          void navigate({
            to: '/dashboard',
            search: {
              period: nextPeriod,
              from: undefined,
              to: undefined,
            },
          });
        }}
      />

      {period === 'custom' || isCustomRangeOpen ? (
        <Popover open={isCustomRangeOpen} onOpenChange={setCustomRangeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              id="dashboard-date-picker-range"
              className="justify-start px-2.5 font-normal"
            >
              <CalendarIcon data-icon="inline-start" />
              {formatCustomDateRangeButton(selectedCustomRange)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={selectedCustomRange?.from}
              selected={selectedCustomRange}
              onSelect={setCustomRange}
              numberOfMonths={2}
            />
            <div className="flex flex-col gap-2 border-t p-3">
              <p className="text-muted-foreground text-xs">
                {t('dashboard.actions.manila_note')}
              </p>
              <Button
                className="w-full"
                disabled={!isCustomRangeValid}
                onClick={() => {
                  if (!customRangeFrom || !customRangeTo) return;
                  void navigate({
                    to: '/dashboard',
                    search: {
                      period: 'custom',
                      from: toManilaDayBoundaryIso(customRangeFrom, 'start'),
                      to: toManilaDayBoundaryIso(customRangeTo, 'end'),
                    },
                  });
                  setCustomRangeOpen(false);
                }}
              >
                {t('dashboard.actions.apply_range')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
